import { useState, type ReactNode } from 'react';
import {
  IMPRESSION_TAG_LABELS,
  IMPRESSION_TAGS,
  IMPRESSION_VERDICT_LABELS,
  type ImpressionPayload,
  type ImpressionTag,
  type ImpressionVerdict,
} from '../guestImpressionsApi';
import '../impressions.css';

type ImpressionEditorInitial = Omit<ImpressionPayload, 'verdict'> & {
  verdict: ImpressionVerdict | null;
};

type ImpressionEditorProps = {
  title: string;
  initial?: ImpressionEditorInitial;
  submitLabel: string;
  submitting: boolean;
  submitDisabled?: boolean;
  fieldErrors?: Record<string, string[]>;
  screenFlash?: string | null;
  mediaSlot?: ReactNode;
  onSubmit: (payload: ImpressionPayload) => void;
  onCancel: () => void;
};

const EMPTY: ImpressionEditorInitial = {
  verdict: null,
  text: null,
  tags: [],
  is_public: true,
};

export function ImpressionEditor({
  title,
  initial = EMPTY,
  submitLabel,
  submitting,
  submitDisabled = false,
  fieldErrors = {},
  screenFlash = null,
  mediaSlot = null,
  onSubmit,
  onCancel,
}: ImpressionEditorProps) {
  const [verdict, setVerdict] = useState<ImpressionVerdict | null>(initial.verdict);
  const [text, setText] = useState(initial.text ?? '');
  const [tags, setTags] = useState<ImpressionTag[]>(initial.tags);
  const [isPublic, setIsPublic] = useState(initial.is_public);
  const [localFlash, setLocalFlash] = useState<string | null>(null);
  const [localVerdictError, setLocalVerdictError] = useState<string | null>(null);

  const toggleTag = (tag: ImpressionTag) => {
    setLocalFlash(null);

    if (tags.includes(tag)) {
      setTags((current) => current.filter((item) => item !== tag));
      return;
    }

    if (tags.length >= 3) {
      setLocalFlash('Максимум 3 тега');
      return;
    }

    setTags((current) => [...current, tag]);
  };

  const submit = () => {
    if (!verdict) {
      setLocalVerdictError('Выберите ответ');
      return;
    }

    setLocalVerdictError(null);
    onSubmit({
      verdict,
      text: text.trim() ? text.trim() : null,
      tags,
      is_public: isPublic,
    });
  };

  const verdictError = localVerdictError ?? fieldErrors.verdict?.[0] ?? null;

  return (
    <main className="impression-screen" aria-label={title}>
      <section className="impression-form-card">
        <div className="impression-screen-heading">
          <span className="impression-eyebrow">После подтверждённого визита</span>
          <h1>{title}</h1>
          <p>Коротко отметьте, понравилось ли место. Подробности и теги — по желанию.</p>
        </div>

        {(screenFlash || localFlash) && (
          <div className="impression-flash is-error" role="status">
            {screenFlash ?? localFlash}
          </div>
        )}

        <fieldset className="impression-fieldset">
          <legend>Вернулись бы сюда?</legend>
          <div className="impression-verdict-selector">
            {(['yes', 'maybe', 'no'] as ImpressionVerdict[]).map((value) => (
              <button
                className={`impression-verdict-pill is-${value}${verdict === value ? ' is-selected' : ''}`}
                key={value}
                type="button"
                aria-pressed={verdict === value}
                onClick={() => {
                  setVerdict(value);
                  setLocalVerdictError(null);
                }}
              >
                {IMPRESSION_VERDICT_LABELS[value]}
              </button>
            ))}
          </div>
          {verdictError && <span className="impression-field-error">{verdictError}</span>}
        </fieldset>

        <label className="impression-field">
          <span>Подробнее</span>
          <textarea
            maxLength={2000}
            rows={6}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Расскажите подробнее (необязательно)"
          />
          <small>{text.length} / 2000</small>
          {fieldErrors.text?.[0] && <span className="impression-field-error">{fieldErrors.text[0]}</span>}
        </label>

        <fieldset className="impression-fieldset">
          <legend>Что особенно запомнилось? <small>до 3</small></legend>
          <div className="impression-tags">
            {IMPRESSION_TAGS.map((tag) => (
              <button
                className={tags.includes(tag) ? 'impression-tag-chip is-selected' : 'impression-tag-chip'}
                key={tag}
                type="button"
                aria-pressed={tags.includes(tag)}
                onClick={() => toggleTag(tag)}
              >
                {IMPRESSION_TAG_LABELS[tag]}
              </button>
            ))}
          </div>
          {fieldErrors.tags?.[0] && <span className="impression-field-error">{fieldErrors.tags[0]}</span>}
        </fieldset>

        {mediaSlot}

        <label className="impression-public-toggle">
          <input
            type="checkbox"
            checked={isPublic}
            onChange={(event) => setIsPublic(event.target.checked)}
          />
          <span className="impression-toggle-track" aria-hidden="true"><span /></span>
          <span>
            <strong>Показывать публично</strong>
            <small>Публичные впечатления видны всем гостям и заведению</small>
          </span>
        </label>
        {fieldErrors.is_public?.[0] && <span className="impression-field-error">{fieldErrors.is_public[0]}</span>}

        <div className="impression-form-actions">
          <button className="impression-secondary-button" type="button" onClick={onCancel} disabled={submitting}>
            Отмена
          </button>
          <button className="impression-primary-button" type="button" onClick={submit} disabled={submitting || submitDisabled}>
            {submitting ? 'Сохраняем…' : submitLabel}
          </button>
        </div>
      </section>
    </main>
  );
}
